import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header, Avatar } from '../../ui';
import theme from '../../ui/theme';
import {
  useSetMenuSliderToggle,
  useSetMenuTocToggle,
  useSetMenuEditLeftToggle,
  useSetLayoutOrganizerToggle,
  useSetEditRightPanelMode,
  useSetMenuEditRightToggle,
  useSetLoginModal,
} from './hooks';
import { selectStoreName, selectUserTheme, selectOwnerName } from '../webshop/globalSlice';
import { selectCartItemsCount } from '../webshop/cartSlice';
import { useUser, useIsLoggedIn } from '../auth/hooks';
import { useModule } from '../../hooks/readFromUrls';
import { MODULES_LIST } from '../../utils/constants';
import { selectCourseTheme, useCourseCanEdit } from '../course/hooks';
import { useFeatures } from '../settings/hooks';

// Menu button
const ProfileAvatar = styled.div`
  z-index: 1315;
  position: fixed;
  left: 14px;
  top: 10px;
  cursor: pointer;
  margin-right: 0.5rem;
`;

const HeaderContainer = ({ noHeader }) => {
  const navigate = useNavigate();
  const setMenuSliderToggle = useSetMenuSliderToggle();
  const setLoginModal = useSetLoginModal();
  const setMenuTocToggle = useSetMenuTocToggle();
  const setMenuEditLeftToggle = useSetMenuEditLeftToggle();
  const setLayoutOrganizerToggle = useSetLayoutOrganizerToggle();
  const setEditRightPanelMode = useSetEditRightPanelMode();
  const setMenuEditRightToggle = useSetMenuEditRightToggle();
  const { t } = useTranslation();

  const storeName = useSelector(selectStoreName);
  const storeTheme = useSelector(selectUserTheme);
  const courseTheme = useSelector(selectCourseTheme);
  const cartItemsCount = useSelector(selectCartItemsCount);
  const ownerName = useSelector(selectOwnerName);

  const module = useModule();
  const user = useUser();
  const loggedIn = useIsLoggedIn();
  const features = useFeatures();
  const location = useLocation();
  const canEdit = useCourseCanEdit();

  const openLoginModal = useCallback(() => {
    setLoginModal(true);
  }, [setLoginModal]);

  const openCart = useCallback(() => {
    navigate(`/store/${storeName}/checkout`);
  }, [storeName, navigate]);

  const goToHome = useCallback(() => {
    navigate(`/store/${storeName}`);
  }, [navigate, storeName]);

  const handleOpenUserMenu = useCallback(() => {
    setMenuSliderToggle(true);
  }, [setMenuSliderToggle]);

  const handleOpenTocMenu = useCallback(() => {
    setMenuTocToggle(true);
  }, [setMenuTocToggle]);

  const handleOpenEditLeftPanel = useCallback(() => {
    setMenuEditLeftToggle(true);
  }, [setMenuEditLeftToggle]);

  const handleOpenLayoutOrganizer = useCallback(() => {
    setLayoutOrganizerToggle(true);
  }, [setLayoutOrganizerToggle]);

  const handleOpenCourseSettings = useCallback(() => {
    setEditRightPanelMode('course');
    setMenuEditRightToggle(true);
  }, [setEditRightPanelMode, setMenuEditRightToggle]);

  const handleToggleEditView = useCallback(() => {
    const parts = location.pathname.split('/');
    const courseId = parts[3];
    const pageId = parts[4];
    if (module === 'view') {
      navigate(`/edit/course/${courseId}${pageId ? `/${pageId}` : ''}`);
    } else if (module === 'edit') {
      navigate(`/view/course/${courseId}${pageId ? `/${pageId}` : ''}`);
    }
  }, [module, location.pathname, navigate]);

  const title = useMemo(() => {
    if (module === 'store' || module === 'store_edit') return [];
    const title = MODULES_LIST[module]?.label(t);
    return [title];
  }, [module, t]);

  const headerProps = useMemo(() => {
    return {
      title,
      headerLogo:
        module === 'store'
          ? storeTheme?.logoUrl
          : module === 'view' || module === 'edit'
            ? courseTheme?.logoUrl
            : null,
      cartItems: cartItemsCount,
      goToHome,
      goToLink: navigate,
      openCart,
      inStore: module === 'store',
      inCourseView: module === 'view',
      inCourseEdit: module === 'edit',
      courseThemeColor: module === 'view' || module === 'edit' ? courseTheme?.color : null,
      ownerName,
      openTocMenu: handleOpenTocMenu,
      openEditLeftPanel: handleOpenEditLeftPanel,
      openCourseSettings: handleOpenCourseSettings,
      openLayoutOrganizer: handleOpenLayoutOrganizer,
      loggedIn,
      openLoginModal,
      userInfo: user,
      bgColor: module && ['course'].includes(module) && theme.colors.COURSIO,
      onToggleEditView: features?.modernCourseBuilder && canEdit ? handleToggleEditView : null,
    };
  }, [
    navigate,
    openCart,
    openLoginModal,
    ownerName,
    title,
    storeTheme,
    courseTheme,
    cartItemsCount,
    module,
    loggedIn,
    user,
    goToHome,
    handleOpenTocMenu,
    handleOpenEditLeftPanel,
    handleOpenCourseSettings,
    handleOpenLayoutOrganizer,
    features?.modernCourseBuilder,
    canEdit,
    handleToggleEditView,
  ]);

  return (
    <>
      {loggedIn && (
        <ProfileAvatar onClick={handleOpenUserMenu}>
          <Avatar url={user?.avatarUrl} name={user?.displayName} size='small' />
        </ProfileAvatar>
      )}
      {!noHeader && <Header {...headerProps} />}
    </>
  );
};

export default HeaderContainer;
